import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Film, Briefcase, User } from 'lucide-react';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import { needsSocialProfileChoice, redirectAfterAuth } from '@/utils/oauth';

const PENDING_REFERRAL_KEY = 'pending_referral_code';

const EscolherPerfilSocialContent = () => {
  const navigate = useNavigate();
  const { user, updateUserLocal } = useAuth();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');

  useEffect(() => {
    if (user && !needsSocialProfileChoice(user)) {
      redirectAfterAuth(user, navigate);
    }
  }, [user, navigate]);

  useEffect(() => {
    try {
      const code = localStorage.getItem(PENDING_REFERRAL_KEY)?.trim().toLowerCase() || '';
      setReferralCode(code);
    } catch {
      /* ignore */
    }
  }, []);

  const handleChoose = async (role) => {
    setError('');
    setLoading(role);
    try {
      const needsApple = user?.apple_profile_completed === false;
      const needsGoogle = user?.google_profile_completed === false;
      if (!needsApple && !needsGoogle) {
        redirectAfterAuth(user, navigate);
        return;
      }
      const refCode = role === 'videomaker' ? referralCode : null;
      let data;
      if (needsApple && needsGoogle) {
        const preferApple = Boolean(user?.apple_user_id);
        const res = preferApple
          ? await authAPI.appleCompleteProfile(role, refCode)
          : await authAPI.googleCompleteProfile(role, refCode);
        data = res.data;
      } else if (needsApple) {
        data = (await authAPI.appleCompleteProfile(role, refCode)).data;
      } else {
        data = (await authAPI.googleCompleteProfile(role, refCode)).data;
      }
      if (role === 'videomaker' && refCode) {
        try {
          localStorage.removeItem(PENDING_REFERRAL_KEY);
        } catch {
          /* ignore */
        }
      }
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      updateUserLocal(data.user);
      redirectAfterAuth(data.user, navigate);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Não foi possível definir o perfil.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-full flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <Card className="shadow-2xl border-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-3">
              <Film className="w-7 h-7 text-primary" aria-hidden />
            </div>
            <CardTitle className="text-2xl font-bold">Como você quer usar o Ufokko?</CardTitle>
            <CardDescription>
              Escolha o tipo de perfil para continuar. Você poderá contratar vídeos ou trabalhar como videomaker.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}

            <button
              type="button"
              disabled={Boolean(loading)}
              onClick={() => handleChoose('client')}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-primary/50 hover:bg-primary/5 transition-colors text-left disabled:opacity-60"
            >
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shrink-0">
                <User className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 dark:text-white">Sou cliente</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Quero contratar videomakers para eventos e conteúdo.
                </p>
              </div>
              {loading === 'client' ? (
                <span className="text-sm text-primary">…</span>
              ) : null}
            </button>

            <button
              type="button"
              disabled={Boolean(loading)}
              onClick={() => handleChoose('videomaker')}
              className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-600 hover:border-primary/50 hover:bg-primary/5 transition-colors text-left disabled:opacity-60"
            >
              <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/40 flex items-center justify-center shrink-0">
                <Briefcase className="w-6 h-6 text-violet-600 dark:text-violet-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 dark:text-white">Sou videomaker</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Quero receber jobs e entregar vídeos pela plataforma.
                </p>
              </div>
              {loading === 'videomaker' ? (
                <span className="text-sm text-primary">…</span>
              ) : null}
            </button>

            {referralCode ? (
              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                Código de indicação ({referralCode}) será aplicado se você escolher videomaker.
              </p>
            ) : null}

            <Button type="button" variant="ghost" className="w-full" onClick={() => navigate('/login')}>
              Voltar ao login
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

const EscolherPerfilSocial = () => (
  <ProtectedRoute>
    <EscolherPerfilSocialContent />
  </ProtectedRoute>
);

export default EscolherPerfilSocial;
